import React, { useEffect, useState } from "react";
import Breadcrumbs from "components/Common/Breadcrumb";
import axiosInstance from "Axiosinstance";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

const issueQrBagPolicySchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Please enter email"),
});

const IssueQrBagTagPlan = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [issuedPolicies, setIssuedPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(true);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const fetchIssuedPolicies = async () => {
    setIsLoadingPolicies(true);

    try {
      const { data } = await axiosInstance.get("/api/partned/issueqrbagpolicy");
      setIssuedPolicies(data?.issuedPolicies || []);
    } catch (error) {
      setIssuedPolicies([]);
      toast.error(
        error?.response?.data?.message ||
          "Unable to fetch issued QR Bag policies",
        {
          position: "top-center",
        }
      );
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  useEffect(() => {
    fetchIssuedPolicies();
  }, []);

  const formatDate = value => {
    if (!value) {
      return "-";
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Partner Portal"
          breadcrumbItem="Issue QR Bag Tag with Bag Protection Plan"
        />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                  <div>
                    <h4 className="mb-1">
                      Issue QR Bag Tag with Bag Protection Plan
                    </h4>
                    <p className="text-muted mb-0">
                      Issue an insurance-enabled QR bag tag for a partner
                      customer.
                    </p>
                  </div>
                  <Button color="primary" onClick={toggleModal}>
                    Issue Insurance
                  </Button>
                </div>

                <div className="border rounded p-4 bg-light">
                  <p className="mb-2">Suggested content for this page:</p>
                  <ul className="mb-0">
                    <li>QR bag tag generation</li>
                    <li>Plan bundling options</li>
                    <li>Combined issue confirmation</li>
                  </ul>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <h5 className="mb-1">Issued QR Bag Policies</h5>
                      <p className="text-muted mb-0">
                        All QR bag policies issued by the logged-in partner.
                      </p>
                    </div>
                    <Button
                      color="light"
                      className="border"
                      onClick={fetchIssuedPolicies}
                      disabled={isLoadingPolicies}
                    >
                      Refresh
                    </Button>
                  </div>

                  {isLoadingPolicies ? (
                    <div className="text-center py-4 text-muted">
                      Loading issued QR Bag policies...
                    </div>
                  ) : issuedPolicies.length === 0 ? (
                    <div className="text-center py-4 border rounded text-muted bg-light">
                      There are no qrbag palicy issued by the partner
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Tag Number</th>
                            <th>Email</th>
                            <th>Issued Date</th>
                            <th>Tag Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issuedPolicies.map((item, index) => (
                            <tr key={item._id || `${item.tag_number}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{item.tag_number || "-"}</td>
                              <td>{item.email || "-"}</td>
                              <td>{formatDate(item.issued_date)}</td>
                              <td>{item.tag_status || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={isModalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal} tag="h4">
            Issue Insurance
          </ModalHeader>
          <ModalBody>
            <Formik
              initialValues={{
                email: "",
              }}
              validationSchema={issueQrBagPolicySchema}
              onSubmit={async (values, { resetForm, setSubmitting }) => {
                try {
                  const { data } = await axiosInstance.post(
                    "/api/partner/issueqrbagpolicy",
                    {
                      email: values.email,
                    }
                  );

                  if (data?.status) {
                    toast.success(
                      data?.message ||
                        `Insurance issued successfully. Tag: ${data?.tag_number}`,
                      {
                        position: "top-center",
                      }
                    );
                    await fetchIssuedPolicies();
                    resetForm();
                    toggleModal();
                  } else {
                    toast.error(
                      data?.message || "Unable to issue insurance policy",
                      {
                        position: "top-center",
                      }
                    );
                  }
                } catch (error) {
                  toast.error(
                    error?.response?.data?.message ||
                      error?.response?.data?.msg ||
                      "Something went wrong",
                    {
                      position: "top-center",
                    }
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <div className="mb-3">
                    <Label className="form-label">Email</Label>
                    <Field
                      name="email"
                      type="email"
                      className={
                        "form-control" +
                        (errors.email && touched.email ? " is-invalid" : "")
                      }
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="text-end">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default IssueQrBagTagPlan;
